import { FC, useEffect, useMemo } from 'react'
import { Link as RouterLink, matchPath, RouteObject, useLocation } from 'react-router'

import { Breadcrumbs as MUIbreadcrumbs, Link, Typography } from '@mui/material'
import { useBreadcrumbStore } from '@/stores/useBreadcrumbStore'

import { BreadcrumbSkeleton } from './BreadcrumbSkeleton'
import { extractRoutePatterns } from './extractRoutePatterns'

type Props = {
  routes: RouteObject[]
  labelMap?: Record<string, string>
}

/**
 * Helper function to format text by removing hyphens and capitalizing words
 * @param {string} text - The text to format
 * @returns {string} The formatted text with hyphens replaced by spaces and each word capitalized
 */
export const kebabCaseToSentence = (text: string): string => {
  return text
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const Breadcrumbs: FC<Props> = ({ routes, labelMap = {} }) => {
  const { pathname } = useLocation()
  const { getDynamicLabel, getLoadingState, setRoutes } = useBreadcrumbStore()

  const routePatterns = useMemo(() => extractRoutePatterns(routes), [routes])

  useEffect(() => {
    setRoutes(routes)
  }, [routes, setRoutes])

  const matchingPattern = routePatterns
    .filter((pattern) => matchPath(pattern, pathname))
    .sort((a, b) => {
      const aSegments = a.split('/').filter(Boolean)
      const bSegments = b.split('/').filter(Boolean)
      return bSegments.length - aSegments.length
    })[0]

  const pathSegments = pathname.split('/').filter(Boolean)

  const breadcrumbItems = pathSegments.map((segment, index) => {
    const path = `/${pathSegments.slice(0, index + 1).join('/')}`

    const loadingState = getLoadingState(segment)

    const dynamicLabel = getDynamicLabel(segment)

    let isParam = false

    if (matchingPattern) {
      const patternSegments = matchingPattern.split('/').filter(Boolean)
      if (index < patternSegments.length && patternSegments[index]?.startsWith(':')) {
        isParam = true
      }
    }

    let label: React.ReactNode

    if (loadingState?.isLoading) {
      label = (
        <BreadcrumbSkeleton minWidth={loadingState.minWidth} maxWidth={loadingState.maxWidth} />
      )
    } else if (dynamicLabel) {
      label = dynamicLabel
    } else if (isParam) {
      label = kebabCaseToSentence(segment)
    } else {
      label = labelMap[segment] || kebabCaseToSentence(segment)
    }

    const isLast = index === pathSegments.length - 1

    if (isLast) {
      return (
        <Typography key={path} color="textPrimary">
          {label}
        </Typography>
      )
    }

    return (
      <Link key={path} component={RouterLink} to={path} color="inherit" underline="hover">
        {label}
      </Link>
    )
  })

  breadcrumbItems.unshift(
    <Link
      key="home"
      component={RouterLink}
      to="/"
      color="inherit"
      underline="hover"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      Home
    </Link>
  )

  return (
    <MUIbreadcrumbs separator="›" aria-label="breadcrumb">
      {breadcrumbItems}
    </MUIbreadcrumbs>
  )
}
